import { useEffect, useRef, useState } from "react";
import type { Room } from "livekit-client";

import {
  addVoiceMember,
  cancelVoiceRequest,
  endVoiceSession,
  getLiveKitGrant,
  getVoiceState,
  grantTemporaryVoice,
  requestVoice,
  resolveVoiceRequest,
  revokeTemporaryVoice,
  startVoiceSession,
  type SessionUser,
  type VoiceSessionView,
  type VoiceStateView,
} from "./gordonland-api.js";

interface VoicePanelProps {
  readonly user: SessionUser;
}

function minutesRemaining(expiresAt: number, serverTime: number): number {
  return Math.max(0, Math.ceil((expiresAt - serverTime) / 60));
}

function splitNicknames(value: string): string[] {
  return value.split(/[，,]/u).map((item) => item.trim()).filter((item) => item.length > 0);
}

export function VoicePanel({ user }: VoicePanelProps) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<VoiceStateView | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [requestNote, setRequestNote] = useState("");
  const [memberNames, setMemberNames] = useState("");
  const [delegateName, setDelegateName] = useState("");
  const [activeSession, setActiveSession] = useState<VoiceSessionView | null>(null);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(false);
  const roomRef = useRef<Room | null>(null);
  const audioHostRef = useRef<HTMLDivElement>(null);

  async function refresh(silent = false): Promise<void> {
    try {
      setState(await getVoiceState());
      if (!silent) setMessage("");
    } catch (error: unknown) {
      if (!silent) setMessage(error instanceof Error ? error.message : "无法读取语音状态");
    }
  }

  async function mutate(action: () => Promise<void>, success: string): Promise<void> {
    if (busy) return;
    setBusy(true);
    setMessage("");
    try {
      await action();
      setMessage(success);
      await refresh(true);
    } catch (error: unknown) {
      setMessage(error instanceof Error ? error.message : "操作失败，请重试");
    } finally {
      setBusy(false);
    }
  }

  async function leaveVoice(): Promise<void> {
    const room = roomRef.current;
    roomRef.current = null;
    setActiveSession(null);
    setMicrophoneEnabled(false);
    if (room !== null) await room.disconnect();
    audioHostRef.current?.replaceChildren();
  }

  async function joinVoice(session: VoiceSessionView): Promise<void> {
    if (busy) return;
    setBusy(true);
    setMessage("");
    try {
      await leaveVoice();
      const grant = await getLiveKitGrant(session.id);
      const { Room: LiveKitRoom, RoomEvent } = await import("livekit-client");
      const room = new LiveKitRoom({ adaptiveStream: true, dynacast: true });
      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind !== "audio") return;
        const element = track.attach();
        element.autoplay = true;
        audioHostRef.current?.append(element);
      });
      room.on(RoomEvent.TrackUnsubscribed, (track) => track.detach().forEach((element) => element.remove()));
      room.on(RoomEvent.Disconnected, () => {
        if (roomRef.current === room) {
          roomRef.current = null;
          setActiveSession(null);
          setMicrophoneEnabled(false);
        }
      });
      await room.connect(grant.serverUrl, grant.token);
      await room.startAudio();
      await room.localParticipant.setMicrophoneEnabled(true);
      roomRef.current = room;
      setActiveSession(session);
      setMicrophoneEnabled(true);
      setMessage("即时语音已连接，麦克风已开启。");
    } catch (error: unknown) {
      await leaveVoice();
      setMessage(error instanceof Error ? error.message : "语音连接失败");
    } finally {
      setBusy(false);
    }
  }

  async function toggleMicrophone(): Promise<void> {
    const room = roomRef.current;
    if (room === null) return;
    const next = !microphoneEnabled;
    try {
      await room.localParticipant.setMicrophoneEnabled(next);
      setMicrophoneEnabled(next);
    } catch (error: unknown) {
      setMessage(error instanceof Error ? error.message : "无法切换麦克风");
    }
  }

  useEffect(() => {
    if (!open) return;
    const initial = window.setTimeout(() => { void refresh(); }, 0);
    const timer = window.setInterval(() => { void refresh(true); }, 15_000);
    return () => {
      window.clearTimeout(initial);
      window.clearInterval(timer);
    };
  }, [open]);

  useEffect(() => () => {
    const room = roomRef.current;
    roomRef.current = null;
    if (room !== null) void room.disconnect();
  }, []);

  const pending = state?.requests[0] ?? null;
  const canStart = user.role === "owner" || (state?.delegations.length ?? 0) > 0;

  return (
    <>
      <button className="voice-launch" type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <span aria-hidden="true">◉</span>
        {activeSession === null ? "即时语音" : microphoneEnabled ? "语音中" : "已静音"}
      </button>
      {open ? (
        <aside className="voice-panel" aria-label="GordonLand 即时语音">
          <header>
            <div><small>LIVEKIT · OWNER CONTROLLED</small><h2>即时语音</h2></div>
            <button type="button" aria-label="关闭语音面板" onClick={() => setOpen(false)}>×</button>
          </header>
          <p className="voice-policy">
            {user.role === "owner"
              ? "只有你可以直接开启语音、批准申请或向在线朋友授予临时主持权。"
              : "未得到 Gordon 批准或临时授权，任何玩家都无法创建 LiveKit 通话。"}
          </p>

          {activeSession !== null ? (
            <section className="voice-connected" aria-live="polite">
              <strong>已连接 · {activeSession.creatorNickname} 发起</strong>
              <div>
                <button type="button" onClick={() => { void toggleMicrophone(); }}>{microphoneEnabled ? "关闭麦克风" : "开启麦克风"}</button>
                <button type="button" onClick={() => { void leaveVoice(); }}>离开通话</button>
              </div>
            </section>
          ) : null}

          {user.role === "owner" && (state?.requests.length ?? 0) > 0 ? (
            <section>
              <h3>待审批申请</h3>
              <div className="voice-list">
                {state?.requests.map((item) => (
                  <article key={item.id}>
                    <strong>{item.requesterNickname}</strong>
                    <span>{item.note || "希望开始即时语音"}</span>
                    <div>
                      <button disabled={busy} type="button" onClick={() => { void mutate(() => resolveVoiceRequest(item.id, "approve"), `已批准 ${item.requesterNickname} 的通话`); }}>批准并开始</button>
                      <button disabled={busy} type="button" onClick={() => { void mutate(() => resolveVoiceRequest(item.id, "deny"), "申请已拒绝"); }}>拒绝</button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          {user.role !== "owner" ? (
            <section>
              <h3>向 Gordon 申请通话</h3>
              {pending !== null ? (
                <div className="voice-pending"><span>申请等待审批中</span><button disabled={busy} type="button" onClick={() => { void mutate(() => cancelVoiceRequest(pending.id), "申请已取消"); }}>取消申请</button></div>
              ) : (
                <div className="voice-form-row">
                  <input aria-label="通话申请说明" maxLength={120} placeholder="简单说明通话需要（可选）" value={requestNote} onChange={(event) => setRequestNote(event.currentTarget.value)} />
                  <button disabled={busy} type="button" onClick={() => { void mutate(() => requestVoice(requestNote), "申请已发送给 Gordon"); }}>发送申请</button>
                </div>
              )}
            </section>
          ) : null}

          {canStart ? (
            <section>
              <h3>{user.role === "owner" ? "开启朋友通话" : "使用临时主持权"}</h3>
              <div className="voice-form-row">
                <input aria-label="邀请朋友昵称" placeholder="朋友昵称，多个用逗号分隔" value={memberNames} onChange={(event) => setMemberNames(event.currentTarget.value)} />
                <button disabled={busy} type="button" onClick={() => { void mutate(() => startVoiceSession(splitNicknames(memberNames), 60), "通话已开启"); }}>开启 60 分钟</button>
              </div>
            </section>
          ) : null}

          {user.role === "owner" ? (
            <section>
              <h3>临时指定在线朋友</h3>
              <div className="voice-form-row">
                <input aria-label="临时主持人昵称" list="online-friends" placeholder="在线朋友昵称" value={delegateName} onChange={(event) => setDelegateName(event.currentTarget.value)} />
                <datalist id="online-friends">{state?.onlineFriends.map((friend) => <option key={friend.id} value={friend.nickname} />)}</datalist>
                <button disabled={busy || delegateName.trim() === ""} type="button" onClick={() => { void mutate(() => grantTemporaryVoice(delegateName, 30), `已授权 ${delegateName} 30 分钟`); }}>授权 30 分钟</button>
              </div>
              {(state?.delegations.length ?? 0) > 0 ? <div className="voice-chips">{state?.delegations.map((delegation) => <button key={delegation.id} disabled={busy} type="button" onClick={() => { void mutate(() => revokeTemporaryVoice(delegation.id), "临时授权已撤销"); }}>{delegation.nickname} · {minutesRemaining(delegation.expiresAt, state?.serverTime ?? 0)} 分钟 ×</button>)}</div> : null}
            </section>
          ) : null}

          <section>
            <h3>可加入的通话</h3>
            {(state?.sessions.length ?? 0) === 0 ? <p className="voice-empty">当前没有经过批准的通话。</p> : (
              <div className="voice-list">
                {state?.sessions.map((session) => (
                  <article key={session.id}>
                    <strong>{session.creatorNickname} 的通话</strong>
                    <span>剩余约 {minutesRemaining(session.expiresAt, state?.serverTime ?? 0)} 分钟</span>
                    <div>
                      <button disabled={busy} type="button" onClick={() => { void joinVoice(session); }}>加入语音</button>
                      {(user.role === "owner" || session.createdBy === user.id) ? <button disabled={busy} type="button" onClick={() => { void mutate(() => endVoiceSession(session.id), "通话已结束"); }}>结束</button> : null}
                      {user.role === "owner" ? <button disabled={busy} type="button" onClick={() => {
                        const nickname = window.prompt("输入要加入这次通话的朋友昵称");
                        if (nickname?.trim()) void mutate(() => addVoiceMember(session.id, nickname), `已邀请 ${nickname}`);
                      }}>邀请朋友</button> : null}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
          {message !== "" ? <p className="voice-message" role="status">{message}</p> : null}
          <div ref={audioHostRef} className="voice-audio-host" aria-hidden="true" />
        </aside>
      ) : null}
    </>
  );
}
